import React, { FC, useEffect } from 'react';
import { Form, useFormikContext } from 'formik';
import _omit from 'lodash/omit';
import Drawer, { useDrawerNavigation } from '@minecraft.drawer';
import { gqlProfileSearchInput } from '@minecraft.graphql-types';
import { useTranslation } from '@blocs.i18n';
import { FilterValues } from '../shared/types';
import { defaultExcludeFilters } from '../shared/config';
import { excludeInvalidFilters } from '../shared/utils';
import { AutofillByRoleCriteria } from './FilterDrawerAdditionalControls';
import { initialFilters } from './shared/config';
import { useDrawerNavigationSections } from './shared/hooks/useNavigationSections';
import { FilterDrawerProps, FilterDrawerType } from './shared/types';
import { TopFiltersSection, AdditionalFiltersSection } from './FilterDrawerSections';

interface Props {
  filterPermissions: FilterDrawerProps['filterPermissions'];
  isSubmissionContext: FilterDrawerProps['isSubmissionContext'];
  type: FilterDrawerType;
  roleId: FilterDrawerProps['roleId'];
  accountCountryCode: FilterDrawerProps['accountCountryCode'];
  additionalInitialFilters: FilterValues;
  getAdditionalAutofillFilters?: FilterDrawerProps['getAdditionalAutofillFilters'];
  refetchProfileFilterData?: (params: { input: Record<string, any>; resultType: string }) => void;
  appliedQueryInput?: gqlProfileSearchInput;
  testId?: string;
}

const FilterDrawer: FC<Props> = ({
  filterPermissions,
  isSubmissionContext,
  type,
  roleId,
  accountCountryCode,
  additionalInitialFilters,
  getAdditionalAutofillFilters,
  refetchProfileFilterData,
  appliedQueryInput,
  testId,
}) => {
  const { t } = useTranslation();
  const { values, setValues, handleSubmit } = useFormikContext<Record<string, any>>();
  const { handleDrawerNavigationChange, handleActiveWithSelectionCategoriesChange } = useDrawerNavigation();
  const drawerNavigationSections = useDrawerNavigationSections(type);

  const cancelClick = () => {
    setValues({ ...initialFilters[type], ...additionalInitialFilters });
  };

  const renderAdditionalNavigationControls = () => {
    return (
      Boolean(roleId) && (
        <AutofillByRoleCriteria
          type={type}
          roleId={roleId}
          getAdditionalAutofillFilters={getAdditionalAutofillFilters}
        />
      )
    );
  };

  useEffect(() => {
    const cleanValues = _omit(values, defaultExcludeFilters);
    const filtersWithSelection = Object.keys(excludeInvalidFilters(cleanValues));

    handleActiveWithSelectionCategoriesChange(filtersWithSelection);
  }, [values, handleActiveWithSelectionCategoriesChange]);

  useEffect(() => {
    handleDrawerNavigationChange(drawerNavigationSections);
  }, []);

  return (
    <Drawer
      title={t('common:filter.title')}
      cancelLabel={t('common:filter.clearAll')}
      submitLabel={t('common:filter.applyFilters')}
      cancelClick={cancelClick}
      submitClick={handleSubmit}
      renderAdditionalNavigationControls={renderAdditionalNavigationControls}
      testId={testId}
    >
      <Form>
        <TopFiltersSection
          type={type}
          filterPermissions={filterPermissions}
          isSubmissionContext={isSubmissionContext}
          roleId={roleId}
        />
        <AdditionalFiltersSection
          appliedQueryInput={appliedQueryInput}
          refetchProfileFilterData={refetchProfileFilterData}
          type={type}
          accountCountryCode={accountCountryCode}
        />
      </Form>
    </Drawer>
  );
};

export default FilterDrawer;
